/**
 * GA bridge — mirrors the Google Analytics event stream already running on a
 * page into B2Metric, so a customer with an existing GA/GTM setup gets event
 * coverage without instrumenting the SDK separately.
 *
 * Opt-in and disabled by default. See docs/ga-bridge-feasibility.md for what
 * each adapter can and cannot see, and for the consent prerequisites.
 */

import { installDataLayerAdapter } from './adapters/datalayer.js';
import { installGtagAdapter, isGtagAvailable } from './adapters/gtag.js';
import { installNetworkAdapter } from './adapters/network.js';
import { normalizeGaEvent, buildEventFilter, DEFAULT_PII_PARAMS } from './normalize.js';

/** Events that are pure GA plumbing and would only inflate volume. */
const DEFAULT_DENY = ['gtm.js', 'gtm.dom', 'gtm.load', 'gtm.init', 'gtm.init_consent'];

export default class GABridge {
  /**
   * @param {object} tracker the B2MWebSDK instance
   */
  constructor(tracker) {
    this.tracker = tracker;
    this.installed = null;
    this.options = null;
    this._seen = new Map();
    this._count = 0;
  }

  /**
   * Normalize the user-supplied gaBridge config into the shape the adapters use.
   * @param {object|boolean} raw
   * @returns {object}
   */
  static resolveOptions(raw) {
    const input = (raw && typeof raw === 'object') ? raw : {};
    const mode = String(input.mode || 'auto').toLowerCase();

    const options = {
      enabled: raw === true || input.enabled === true,
      mode: ['auto', 'datalayer', 'gtag', 'network'].includes(mode) ? mode : 'auto',
      endpoints: Array.isArray(input.endpoints) ? input.endpoints : [],
      // Exclusive: the bridge is the only behavioural source. The gating itself
      // lives in Config (event groups); kept here for introspection.
      exclusive: input.exclusive === true,
      nameMap: (input.nameMap && typeof input.nameMap === 'object') ? input.nameMap : {},
      eventFilter: input.eventFilter || null,
      // Conservative by default: GA4 Enhanced Measurement auto-fires scrolls and
      // outbound clicks, so an unbounded mirror can multiply event volume.
      maxEventsPerPage: Number.isFinite(input.maxEventsPerPage) ? input.maxEventsPerPage : 500,
      dedupeWindowMs: Number.isFinite(input.dedupeWindowMs) ? input.dedupeWindowMs : 2000,
      // Only forward when Consent Mode reports analytics_storage granted.
      // Turning this off requires the customer's own consent basis.
      requireAnalyticsConsent: input.requireAnalyticsConsent !== false,
      // Merged with the defaults, never replacing them: a customer list that
      // forgets a default must not start leaking it.
      piiParams: [...new Set([...DEFAULT_PII_PARAMS,
        ...(Array.isArray(input.piiParams) ? input.piiParams : [])])]
    };

    const filter = buildEventFilter(options.eventFilter);
    const deny = new Set(DEFAULT_DENY);
    options._filter = (name, params) => !deny.has(name) && filter(name, params);
    options._piiDeny = new Set(options.piiParams.map((k) => String(k).toLowerCase()));

    return options;
  }

  /**
   * Install the bridge. Safe to call when disabled — it simply does nothing.
   * @param {object|boolean} rawConfig value of config.gaBridge
   * @returns {string|null} the adapter that was installed
   */
  install(rawConfig) {
    const options = GABridge.resolveOptions(rawConfig);
    this.options = options;

    if (!options.enabled) return null;
    if (typeof window === 'undefined') return null;
    if (this.installed) return this.installed.name;

    const ctx = {
      win: window,
      endpoints: options.endpoints,
      log: (...args) => this.tracker.logger.log(...args),
      emit: (raw, adapter) => this._emit(raw, adapter)
    };

    const chosen = this._select(options.mode, ctx);
    if (!chosen) {
      this.tracker.logger.log(`[B2M GA bridge] no usable surface for mode '${options.mode}'`);
      return null;
    }

    this.installed = chosen;
    this.tracker.logger.log(`[B2M GA bridge] enabled (adapter: ${chosen.name})`);
    return chosen.name;
  }

  /**
   * Pick the adapter for the requested mode.
   *
   * `auto` order is dataLayer → gtag → network, EXCEPT that the dataLayer
   * surface is skipped when the SDK's own trackingMode listener already owns
   * window.dataLayer — otherwise every push would be ingested twice.
   *
   * @param {string} mode
   * @param {object} ctx
   * @returns {{name: string, uninstall: Function}|null}
   */
  _select(mode, ctx) {
    if (mode === 'datalayer') return installDataLayerAdapter(ctx);
    if (mode === 'gtag') return installGtagAdapter(ctx);
    if (mode === 'network') return installNetworkAdapter(ctx);

    const dataLayerOwnedBySdk = this.tracker.config.shouldUseDataLayerListener();
    if (!dataLayerOwnedBySdk && ctx.win.dataLayer) {
      const dl = installDataLayerAdapter(ctx);
      if (dl) return dl;
    }
    if (isGtagAvailable(ctx.win)) {
      const gtag = installGtagAdapter(ctx);
      if (gtag) return gtag;
    }
    return installNetworkAdapter(ctx);
  }

  /**
   * Normalize one observed GA event and hand it to the SDK's event queue.
   * Never throws: adapters call this from inside the host page's own call stack.
   * @param {object|null} raw
   * @param {string} adapter
   */
  _emit(raw, adapter) {
    try {
      if (!raw) return;

      const normalized = normalizeGaEvent(raw, this.options, adapter);
      if (!normalized) return;

      if (this.options.requireAnalyticsConsent &&
          normalized.params.ga_analytics_consent === false) {
        return;
      }

      if (this._count >= this.options.maxEventsPerPage) return;
      if (this._isDuplicate(normalized.params.ga_bridge_dedupe_key)) return;

      this._count++;
      this.tracker.b2mTracker.trackBridged(normalized.eventName, normalized.params);
    } catch (e) {
      this.tracker.logger.log('[B2M GA bridge] event skipped', e);
    }
  }

  /**
   * @param {string} key
   * @returns {boolean} true when the same event was seen inside the window
   */
  _isDuplicate(key) {
    const now = Date.now();
    const previous = this._seen.get(key);
    if (previous !== undefined && now - previous < this.options.dedupeWindowMs) return true;
    this._seen.set(key, now);
    // Bound the map so a long-lived SPA session cannot grow it without limit.
    if (this._seen.size > 500) {
      const cutoff = now - this.options.dedupeWindowMs;
      this._seen.forEach((ts, k) => { if (ts < cutoff) this._seen.delete(k); });
    }
    return false;
  }

  /** Restore every global this bridge patched. */
  uninstall() {
    if (!this.installed) return;
    try { this.installed.uninstall(); } catch (e) { /* ignore */ }
    this.installed = null;
  }
}
